import { useState, useEffect, useRef, useCallback } from 'react'
import type { UUID } from 'crypto'
import { Box, Typography, Button, Alert } from '@mui/material'
import { STEPS } from '../onboardingSteps'
import { useOnboardingV2 } from '../OnboardingContext'
import { useWizardData } from '../hooks/useWizardData'
import { useToken } from '../hooks/useToken'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { useJobRunner } from '../hooks/useJobRunner'
import { useRunScan } from '../hooks/useRunScan'
import { useAssessFundingNeed } from '../hooks/useAssessFundingNeed'
import { useAppendFundingDescription } from '../hooks/useAppendFundingDescription'
import { useFundingNeeds } from '@/utils/hooks/fundingNeeds'
import ProgressScreen from '../components/ProgressScreen'
import AssessmentGate from '../components/AssessmentGate'
import type { SuggestionItem } from '../components/AssessmentGate'
import type { FundingNeedDataAssessmentResponse } from '@/models/FundingNeeds'
import { primaryButtonSx } from '../styles'

const HELP_TEXTS = [
  "We're now scanning Fundsorter's extensive database of grant opportunities to find the best matches for you!",
  'There are a lot of them, so this can take a few minutes.',
  "Fundsorter matches based on location, eligibility, topic area, criteria, timing, and many other factors. This helps you find and apply for the grants you're most likely to be successful in getting.",
]

type QuestionKey = `question_part_${1 | 2 | 3 | 4 | 5}`
type DescKey = `desc_part_${1 | 2 | 3 | 4 | 5}`

function extractSuggestionItems(
  a: FundingNeedDataAssessmentResponse,
): SuggestionItem[] {
  const items: SuggestionItem[] = []
  for (const n of [1, 2, 3, 4, 5] as const) {
    const label = a[`question_part_${n}` as QuestionKey]
    if (label) items.push({ label, hint: a[`desc_part_${n}` as DescKey] })
  }
  return items
}

export default function ScanRunning() {
  const {
    orgUuid,
    fundingNeedId,
    scanJobId,
    setScanJobId,
    fundingQualityPassed,
    setFundingQualityPassed,
    goNext,
    goBack,
    goToStep,
    setHideNav,
    staleFrom,
    setStaleFrom,
  } = useOnboardingV2()
  const { getToken } = useToken()
  const { token: ctxToken } = getCurrentUser()
  const token = ctxToken ?? ''
  const [wizardData, setWizardData] = useWizardData()
  const runScan = useRunScan()

  // Abort guard — prevents post-unmount goNext/persist calls
  const unmountedRef = useRef(false)
  useEffect(
    () => () => {
      unmountedRef.current = true
    },
    [],
  )

  const assessMutation = useAssessFundingNeed()
  const appendDescription = useAppendFundingDescription()

  // Load current description for gate resubmission
  const { data: fundingNeeds } = useFundingNeeds(
    token,
    orgUuid ? (orgUuid as UUID) : null,
  )
  const currentNeed = fundingNeeds?.find((n) => n.id === fundingNeedId) ?? null
  const descriptionRef = useRef('')
  if (currentNeed?.description) descriptionRef.current = currentNeed.description

  // Mutation refs to avoid stale closures
  const assessRef = useRef(assessMutation)
  assessRef.current = assessMutation
  const appendRef = useRef(appendDescription)
  appendRef.current = appendDescription

  // Reset flag when running a fresh scan (so cancel doesn't false-positive)
  useEffect(() => {
    setWizardData({ scanCompleted: false })
  }, [])

  // ── Payment required state (free tier scan limit hit) ──────
  const [isPaymentRequired, setIsPaymentRequired] = useState(false)

  // ── Assessment state ───────────────────────────────────────
  const [assessmentDone, setAssessmentDone] = useState(fundingQualityPassed)
  const [assessment, setAssessment] =
    useState<FundingNeedDataAssessmentResponse | null>(null)
  const [showGate, setShowGate] = useState(false)
  const [gateInputs, setGateInputs] = useState<Record<number, string>>({})
  const [assessError, setAssessError] = useState<string | null>(null)
  const assessStartedRef = useRef(false)

  const handleAssessmentResult = useCallback(
    async (result: FundingNeedDataAssessmentResponse, _t: string) => {
      if (result.Quality === 'Bad') {
        setAssessment(result)
        setShowGate(true)
        setGateInputs({})
      } else {
        setFundingQualityPassed(true)
        setAssessmentDone(true)
      }
    },
    [setFundingQualityPassed],
  )
  const handleResultRef = useRef(handleAssessmentResult)
  handleResultRef.current = handleAssessmentResult

  const runAssessment = useCallback(async () => {
    if (!fundingNeedId) return
    try {
      const t = await getToken()
      const result = await assessRef.current.mutateAsync({
        token: t,
        needId: fundingNeedId,
      })
      await handleResultRef.current(result, t)
    } catch {
      setAssessError(
        'Something went wrong while preparing your scan. Please try again.',
      )
    }
  }, [getToken, fundingNeedId])

  // Fire assessment on mount (if not already passed)
  useEffect(() => {
    if (
      assessStartedRef.current ||
      fundingQualityPassed ||
      !orgUuid ||
      !fundingNeedId
    )
      return
    assessStartedRef.current = true
    runAssessment()
  }, [orgUuid, fundingNeedId])

  // ── Scan job runner (activates after assessment passes) ────
  const startJob = useCallback(
    async (t: string) => {
      try {
        const result = await runScan.mutateAsync({
          token: t,
          fundingNeedId: fundingNeedId!,
          includeRelational: true,
          isOnboarding: true,
        })
        setScanJobId(result.job_id)
        return result.job_id
      } catch (err) {
        if (err instanceof Error && err.name === 'PaymentRequired') {
          setIsPaymentRequired(true)
          // Returning a dummy string keeps useJobRunner happy; the payment-required
          // UI is shown before SSE would ever connect.
          return ''
        }
        throw err
      }
    },
    [fundingNeedId, runScan, setScanJobId],
  )

  const onScanComplete = useCallback(async () => {
    if (unmountedRef.current) return
    setWizardData({ scanCompleted: true })
    if (staleFrom !== null && staleFrom <= STEPS.SCAN_RUNNING) {
      setStaleFrom(STEPS.DRAFT_GENERATING)
    }
    await goNext()
  }, [goNext, staleFrom, setStaleFrom, setWizardData])

  const {
    progress,
    error: scanError,
    start: startScan,
    retry: retryScan,
  } = useJobRunner({
    sseType: 'scan',
    startJob,
    onComplete: onScanComplete,
    prerequisiteError: !fundingNeedId ? 'No funding need found' : null,
  })

  // Start scan once assessment passes
  useEffect(() => {
    if (assessmentDone) startScan()
  }, [assessmentDone, startScan])

  // Hide shell nav during progress, show during gate
  useEffect(() => {
    setHideNav(!showGate)
    return () => setHideNav(false)
  }, [showGate, setHideNav])

  // Submit the gate answers and proceed to the scan. AssessmentGate drives the
  // shell's single forward button and calls this once the user advances past
  // the last question.
  const handleGateSubmit = useCallback(
    async (combinedText: string) => {
      setShowGate(false)
      setAssessment(null)

      try {
        // Append any user-provided text before proceeding
        if (combinedText.trim()) {
          const t = await getToken()
          await appendRef.current.mutateAsync({
            token: t,
            needId: fundingNeedId!,
            currentDescription: descriptionRef.current,
            additionalText: combinedText,
          })
          descriptionRef.current = descriptionRef.current
            ? `${descriptionRef.current}\n\n${combinedText}`
            : combinedText
        }

        // Proceed directly to scan — no re-assessment
        setFundingQualityPassed(true)
        setAssessmentDone(true)
      } catch {
        setAssessError(
          'Something went wrong while preparing your scan. Please try again.',
        )
      }
    },
    [getToken, fundingNeedId, setFundingQualityPassed],
  )

  // ── Render ─────────────────────────────────────────────────
  if (showGate && assessment) {
    return (
      <AssessmentGate
        heading="Your funding request needs more detail"
        items={extractSuggestionItems(assessment)}
        quality={assessment.Quality}
        inputs={gateInputs}
        onInputChange={(i, v) => setGateInputs((prev) => ({ ...prev, [i]: v }))}
        onSubmit={handleGateSubmit}
      />
    )
  }

  // Guard: scan limit hit (402) — surface a helpful message instead of a retry loop
  if (isPaymentRequired || wizardData.scanHadResults) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
          px: 4,
          textAlign: 'center',
        }}
      >
        <Alert severity="info" sx={{ mb: 3, maxWidth: 480 }}>
          You've already completed a funding scan. You can't start a new scan
          during onboarding.
        </Alert>
        <Typography
          variant="body2"
          sx={{ mb: 3, maxWidth: 480, color: 'text.secondary' }}
        >
          Head back to view your existing scan results and select a match to
          continue with application drafting.
        </Typography>
        <Button
          disableRipple
          variant="contained"
          onClick={() => goToStep(STEPS.SCAN_RESULTS)}
          sx={primaryButtonSx}
        >
          View your scan results
        </Button>
      </Box>
    )
  }

  const handleRetry = assessError
    ? () => {
        setAssessError(null)
        assessStartedRef.current = false
        runAssessment()
      }
    : retryScan

  return (
    <ProgressScreen
      statusText="Finding funding opportunities that match your request..."
      helpTexts={HELP_TEXTS}
      progress={assessmentDone ? progress : undefined}
      error={assessError || scanError}
      onRetry={handleRetry}
      onBack={() => goBack()}
    />
  )
}
